/**
 * Advanced Rhythm Generator
 * Multiple algorithms for generating diverse rhythmic patterns
 */

import { euclideanRhythm } from './euclidean';

export type RhythmAlgorithm = 
  | 'euclidean' 
  | 'probabilistic' 
  | 'polyrhythm' 
  | 'swing' 
  | 'breakbeat'
  | 'syncopated'
  | 'clustered'
  | 'sparse-random'
  | 'phased';

/**
 * Probabilistic rhythm - each step has independent probability
 */
function probabilisticRhythm(
  steps: number,
  baseProbability: number,
  accentBeats: number[] = [],
  accentBoost: number = 0.3
): boolean[] {
  const pattern: boolean[] = [];
  
  for (let i = 0; i < steps; i++) {
    let prob = baseProbability;
    
    // Boost probability on accent beats (typically downbeats)
    if (accentBeats.includes(i)) {
      prob = Math.min(1, prob + accentBoost);
    }
    
    // Reduce probability on weak beats for more natural feel
    if (i % 4 === 3) { // Last 16th of each beat
      prob *= 0.6;
    }
    
    pattern.push(Math.random() < prob);
  }
  
  return pattern;
}

/**
 * Polyrhythm generator - creates patterns based on multiple pulse ratios
 */
function polyrhythmPattern(
  steps: number,
  pulseRatios: number[],
  offset: number = 0
): boolean[] {
  const pattern: boolean[] = new Array(steps).fill(false);
  
  pulseRatios.forEach(ratio => {
    const spacing = steps / ratio;
    for (let i = 0; i < ratio; i++) {
      const position = Math.floor((i * spacing + offset) % steps);
      pattern[position] = true;
    }
  });
  
  return pattern;
}

/**
 * Swing rhythm - shifts off-beat notes
 */
function swingRhythm(
  steps: number,
  density: number,
  swingAmount: number = 0.66 // Traditional swing is ~0.66
): boolean[] {
  const basePattern = euclideanRhythm(steps, Math.floor(steps * density), 0);
  const swungPattern: boolean[] = new Array(steps).fill(false);
  
  for (let i = 0; i < steps; i++) {
    if (basePattern[i]) {
      // Shift odd-numbered 8th notes later for swing feel
      if (i % 4 === 2) {
        // This is an "and" - potentially shift it
        const shift = Math.random() < swingAmount ? 1 : 0;
        const newPos = Math.min(i + shift, steps - 1);
        swungPattern[newPos] = true;
      } else {
        swungPattern[i] = true;
      }
    }
  }
  
  return swungPattern;
}

/**
 * Breakbeat patterns - inspired by classic breakbeats
 */
function breakbeatPattern(steps: number, variation: number): boolean[] {
  // Classic breakbeat templates (16 steps)
  const templates = [
    // Amen break style
    [true, false, false, false, true, false, true, false, false, false, true, false, true, false, false, true],
    // Funky drummer style
    [true, false, true, false, false, true, false, true, false, false, true, false, false, true, false, false],
    // Think break style
    [true, false, false, true, true, false, false, false, true, false, true, false, false, true, false, false],
    // Apache style
    [true, false, false, false, true, true, false, false, true, false, false, false, true, false, true, false],
    // Impeach the president style
    [true, false, true, false, false, false, true, false, false, true, false, true, false, false, true, false],
    // Skull snaps style
    [true, false, false, true, false, false, true, false, true, false, false, true, false, true, false, false],
  ];
  
  // Select template based on variation
  const templateIndex = Math.floor(variation * templates.length / 100);
  const template = templates[Math.min(templateIndex, templates.length - 1)];
  
  // Add some randomization
  const result = [...template];
  const mutations = Math.floor(variation / 25); // 0-3 mutations
  
  for (let i = 0; i < mutations; i++) {
    const pos = Math.floor(Math.random() * steps);
    // Don't mutate the downbeat (position 0)
    if (pos !== 0) {
      result[pos] = Math.random() > 0.5;
    }
  }
  
  return result;
}

/**
 * Syncopated rhythm - emphasizes off-beats
 */
function syncopatedRhythm(steps: number, intensity: number): boolean[] {
  const pattern: boolean[] = new Array(steps).fill(false);
  const intensityScale = intensity / 100;
  
  for (let i = 0; i < steps; i++) {
    const isDownbeat = i % 4 === 0;
    const isOffbeat = i % 2 === 1;
    const isUpbeat = i % 4 === 2;
    
    let probability: number;
    
    if (isDownbeat) {
      // Downbeats have moderate probability (anchoring)
      probability = 0.4 + intensityScale * 0.2;
    } else if (isOffbeat) {
      // Off-beats (16th notes) get syncopation boost
      probability = 0.1 + intensityScale * 0.5;
    } else if (isUpbeat) {
      // Upbeats (8th note "ands") are emphasized
      probability = 0.3 + intensityScale * 0.4;
    } else {
      probability = 0.1 + intensityScale * 0.3;
    }
    
    pattern[i] = Math.random() < probability;
  }
  
  // Ensure at least some hits
  if (!pattern.some(v => v)) {
    pattern[0] = true;
    pattern[Math.floor(steps / 2)] = true;
  }
  
  return pattern;
}

/**
 * Clustered rhythm - creates groups of notes with gaps
 */
function clusteredRhythm(
  steps: number,
  clusterCount: number,
  clusterSize: number
): boolean[] {
  const pattern: boolean[] = new Array(steps).fill(false);
  
  // Generate cluster start positions (spread across the bar)
  const clusterStarts: number[] = [];
  const spacing = Math.floor(steps / clusterCount);
  
  for (let i = 0; i < clusterCount; i++) {
    const basePos = i * spacing;
    const jitter = Math.floor(Math.random() * (spacing / 2));
    clusterStarts.push((basePos + jitter) % steps);
  }
  
  // Fill in clusters
  clusterStarts.forEach(start => {
    const size = Math.max(1, Math.floor(clusterSize + (Math.random() - 0.5) * 2));
    for (let j = 0; j < size; j++) {
      const pos = (start + j) % steps;
      pattern[pos] = true;
    }
  });
  
  return pattern;
}

/**
 * Sparse random - minimal hits with random placement
 */
function sparseRandomRhythm(steps: number, hitCount: number): boolean[] {
  const pattern: boolean[] = new Array(steps).fill(false);
  const positions = new Set<number>();
  
  // Always include downbeat for anchoring
  positions.add(0);
  
  // Add random positions
  while (positions.size < hitCount) {
    positions.add(Math.floor(Math.random() * steps));
  }
  
  positions.forEach(pos => {
    pattern[pos] = true;
  });
  
  return pattern;
}

/**
 * Phased rhythm - creates patterns that shift over time
 * Returns a pattern that sounds different when layered with similar patterns
 */
function phasedRhythm(
  steps: number,
  pulses: number,
  phaseOffset: number
): boolean[] {
  // Use prime number relationships for interesting phasing
  const primes = [5, 7, 11, 13];
  const selectedPrime = primes[phaseOffset % primes.length];
  
  const pattern: boolean[] = new Array(steps).fill(false);
  
  for (let i = 0; i < pulses; i++) {
    const pos = (i * selectedPrime + phaseOffset) % steps;
    pattern[pos] = true;
  }
  
  return pattern;
}

/**
 * Generate a rhythm pattern using specified algorithm
 */
export function generateRhythm(
  algorithm: RhythmAlgorithm,
  steps: number,
  params: {
    density?: number;      // 0-100
    variation?: number;    // 0-100  
    swing?: number;        // 0-100
    pulses?: number;
    offset?: number;
  } = {}
): boolean[] {
  const { 
    density = 50, 
    variation = 50,
    swing = 0,
    pulses,
    offset = 0
  } = params;
  
  const densityScale = density / 100;
  
  switch (algorithm) {
    case 'euclidean': {
      const numPulses = pulses ?? Math.max(1, Math.floor(densityScale * steps * 0.8));
      const rotation = Math.floor(Math.random() * steps);
      return euclideanRhythm(steps, numPulses, rotation);
    }
    
    case 'probabilistic': {
      const accentBeats = [0, 4, 8, 12]; // Quarter note positions
      return probabilisticRhythm(steps, densityScale * 0.6, accentBeats);
    }
    
    case 'polyrhythm': {
      // Choose interesting polyrhythmic ratios
      const ratioOptions = [
        [3, 4],      // 3 against 4
        [5, 4],      // 5 against 4
        [3, 5],      // 3 against 5
        [7, 4],      // 7 against 4
        [5, 3],      // 5 against 3
        [4, 3, 2],   // Multiple layers
      ];
      const ratioIndex = Math.floor(variation / 100 * ratioOptions.length);
      const ratios = ratioOptions[Math.min(ratioIndex, ratioOptions.length - 1)];
      return polyrhythmPattern(steps, ratios, offset);
    }
    
    case 'swing': {
      const swingAmount = 0.5 + (swing / 100) * 0.33; // 0.5-0.83 swing range
      return swingRhythm(steps, densityScale * 0.5, swingAmount);
    }
    
    case 'breakbeat': {
      return breakbeatPattern(steps, variation);
    }
    
    case 'syncopated': {
      return syncopatedRhythm(steps, variation);
    }
    
    case 'clustered': {
      const clusterCount = Math.max(1, Math.floor(2 + densityScale * 3)); // 2-5 clusters
      const clusterSize = Math.max(1, Math.floor(1 + densityScale * 3)); // 1-4 size
      return clusteredRhythm(steps, clusterCount, clusterSize);
    }
    
    case 'sparse-random': {
      const hitCount = Math.max(1, Math.floor(1 + densityScale * 6)); // 1-7 hits
      return sparseRandomRhythm(steps, hitCount);
    }
    
    case 'phased': {
      const numPulses = Math.max(2, Math.floor(3 + densityScale * 5)); // 3-8 pulses
      return phasedRhythm(steps, numPulses, offset);
    }
    
    default:
      return euclideanRhythm(steps, Math.floor(densityScale * steps), 0);
  }
}

/**
 * Get random algorithm weighted by style preference
 */
export function getRandomAlgorithm(style: 'all' | 'groovy' | 'experimental' | 'minimal' = 'all'): RhythmAlgorithm {
  const algorithmsByStyle: Record<string, RhythmAlgorithm[]> = {
    all: ['euclidean', 'probabilistic', 'polyrhythm', 'swing', 'breakbeat', 'syncopated', 'clustered', 'sparse-random', 'phased'],
    groovy: ['euclidean', 'swing', 'breakbeat', 'syncopated'],
    experimental: ['polyrhythm', 'phased', 'clustered', 'probabilistic'],
    minimal: ['sparse-random', 'euclidean', 'phased'],
  };
  
  const options = algorithmsByStyle[style];
  return options[Math.floor(Math.random() * options.length)];
}

/**
 * Layer-specific rhythm generation with intelligent algorithm selection
 */
export function generateLayerRhythm(
  layer: 'percussion' | 'bass' | 'melody' | 'pad',
  density: number,
  variation: number
): boolean[] {
  const steps = 16;
  
  // Select appropriate algorithms per layer type
  const layerAlgorithms: Record<string, RhythmAlgorithm[]> = {
    percussion: ['breakbeat', 'syncopated', 'euclidean', 'probabilistic', 'polyrhythm'],
    bass: ['euclidean', 'syncopated', 'swing', 'clustered'],
    melody: ['probabilistic', 'syncopated', 'phased', 'sparse-random', 'clustered'],
    pad: ['sparse-random', 'euclidean', 'phased'],
  };
  
  const algorithms = layerAlgorithms[layer];
  const algorithmIndex = Math.floor(Math.random() * algorithms.length);
  const algorithm = algorithms[algorithmIndex];
  
  // Layer-specific density adjustments
  let adjustedDensity = density;
  let adjustedVariation = variation;
  
  switch (layer) {
    case 'percussion':
      // Percussion can be denser
      adjustedDensity = Math.min(100, density * 1.2);
      break;
    case 'bass':
      // Bass should be more grounded
      adjustedDensity = Math.max(20, density * 0.7);
      adjustedVariation = variation * 0.6; // Less wild variation
      break;
    case 'melody':
      // Melody has full range
      adjustedVariation = Math.min(100, variation * 1.2);
      break;
    case 'pad':
      // Pads are sparse
      adjustedDensity = Math.max(10, density * 0.4);
      break;
  }
  
  const pattern = generateRhythm(algorithm, steps, {
    density: adjustedDensity,
    variation: adjustedVariation,
    offset: Math.floor(Math.random() * steps),
  });
  
  // Ensure minimum activity for non-zero density
  if (density > 0 && !pattern.some(v => v)) {
    pattern[0] = true;
  }
  
  return pattern;
}

/**
 * Apply rhythmic transformation to existing pattern
 */
export function transformPattern(
  pattern: boolean[],
  transformation: 'invert' | 'shift' | 'thin' | 'thicken' | 'humanize'
): boolean[] {
  const result = [...pattern];
  const steps = pattern.length;
  
  switch (transformation) {
    case 'invert':
      return result.map(v => !v);
      
    case 'shift':
      const shiftAmount = 1 + Math.floor(Math.random() * 3);
      return [...result.slice(shiftAmount), ...result.slice(0, shiftAmount)];
      
    case 'thin':
      // Remove random hits
      for (let i = 0; i < steps; i++) {
        if (result[i] && Math.random() > 0.6) {
          result[i] = false;
        }
      }
      return result;
      
    case 'thicken':
      // Add some hits near existing ones
      for (let i = 0; i < steps; i++) {
        if (result[i] && Math.random() > 0.7) {
          const neighbor = (i + 1) % steps;
          result[neighbor] = true;
        }
      }
      return result;
      
    case 'humanize':
      // Randomly add/remove a few hits for human feel
      const changes = 1 + Math.floor(Math.random() * 2);
      for (let i = 0; i < changes; i++) {
        const pos = Math.floor(Math.random() * steps);
        if (pos !== 0) { // Don't change downbeat
          result[pos] = Math.random() > 0.5;
        }
      }
      return result;
      
    default:
      return result;
  }
}

/**
 * Combine two patterns with different logical operations
 */
export function combinePatterns(
  pattern1: boolean[],
  pattern2: boolean[],
  operation: 'or' | 'and' | 'xor'
): boolean[] {
  const length = Math.max(pattern1.length, pattern2.length);
  const result: boolean[] = [];
  
  for (let i = 0; i < length; i++) {
    const a = pattern1[i % pattern1.length] ?? false;
    const b = pattern2[i % pattern2.length] ?? false;
    
    switch (operation) {
      case 'or':
        result.push(a || b);
        break;
      case 'and':
        result.push(a && b);
        break;
      case 'xor':
        result.push(a !== b);
        break;
    }
  }
  
  return result;
}
